/**
 * Field catalog for the generic `field_match` condition. Declares the FLAT,
 * stable paths a `field_match` node may read from the two prefetched profiles
 * (`identity` / `payment`) on the run context, the value type of each, and the
 * operators that make sense for that type. The worker (separate task) builds the
 * `IdentityProfile` / `PaymentProfile` on the context using these EXACT keys.
 *
 * The catalog is the single source of truth shared by:
 *  - the definition schema (`field_match` superRefine validates `fieldPath` is a
 *    known path for the chosen `source`, and operator/value consistency),
 *  - the registry evaluator (reads the value via `readPath` + `applyOperator`),
 *  - the admin editor (renders the path dropdown + operator list per field).
 *
 * Adding a readable field is a localized change here + populating it on the
 * worker-built profile.
 */
import { IDENTITY_FIELDS as SHARED_IDENTITY, PAYMENT_FIELDS as SHARED_PAYMENT, } from '@wit-support/data-providers';
/** Where a `field_match` reads from on the run context. */
export const FIELD_SOURCES = ['identity', 'payment', 'billing'];
/**
 * Operators a `field_match` may apply. SINGLE source of truth — `definition.ts`
 * builds its `FieldOperatorSchema` (and `FieldSourceSchema`, from
 * `FIELD_SOURCES` above) from this array rather than keeping a second,
 * manually-synced literal list (see M-067).
 */
export const FIELD_OPERATORS = [
    'exists',
    'not_exists',
    'equals',
    'not_equals',
    'gt',
    'gte',
    'lt',
    'lte',
    'contains',
    'in',
    'is_true',
    'is_false',
];
/** Operators valid for null-aware presence checks — every field supports them. */
const PRESENCE_OPS = ['exists', 'not_exists'];
/** Operators valid for equality on any scalar. */
const EQ_OPS = ['equals', 'not_equals'];
/** Ordered-comparison operators (numeric / date epoch). */
const ORDER_OPS = ['gt', 'gte', 'lt', 'lte'];
const STRING_OPS = [...PRESENCE_OPS, ...EQ_OPS, 'contains', 'in'];
const NUMBER_OPS = [...PRESENCE_OPS, ...EQ_OPS, ...ORDER_OPS, 'in'];
const BOOLEAN_OPS = [...PRESENCE_OPS, 'is_true', 'is_false', ...EQ_OPS];
const DATE_OPS = [...PRESENCE_OPS, ...EQ_OPS, ...ORDER_OPS];
/**
 * The operators each field type supports. The mapping is uniform — a field's
 * type fully determines its operator set — so the pipeline-only `FieldDef`
 * (which adds `operators`) is DERIVED from the canonical `{path,label,type}`
 * defs in `@wit-support/data-providers` by attaching the matching set here.
 * This keeps the field catalog single-sourced (no duplicated path/label/type
 * arrays) while preserving the exact operator sets the schema + evaluator + UI
 * depend on.
 */
const OPS_BY_TYPE = {
    string: STRING_OPS,
    number: NUMBER_OPS,
    boolean: BOOLEAN_OPS,
    date: DATE_OPS,
};
/** Attach the type-derived operator set to each shared field def → `FieldDef`. */
const withOps = (defs) => defs.map((d) => ({ path: d.path, label: d.label, type: d.type, operators: OPS_BY_TYPE[d.type] }));
/** Identity profile fields (mirror `ResolvedIdentity`, flattened onto `IdentityProfile`). */
export const IDENTITY_FIELDS = withOps(SHARED_IDENTITY);
/** Payment profile fields (flattened from `BillingSummary` + `CheckActive` + `CancellationState`). */
export const PAYMENT_FIELDS = withOps(SHARED_PAYMENT);
/**
 * Billing-scale fields («кит»). PIPELINE-ONLY, and deliberately its own source
 * rather than two more entries in {@link PAYMENT_FIELDS}: the payment catalog is
 * derived 1:1 from `@wit-support/data-providers`, which is ALSO the template-token
 * catalog (`{{data.payment.*}}`) and its send-time mapper. Appending there would
 * either desync the catalog from that mapper or mint a customer-facing template
 * token nobody asked for. A separate source is purely additive: existing
 * definitions name `identity` / `payment` and are untouched.
 *
 *  - `succeededChargesCount` — successful charges (subscriptions + one-off
 *    purchases, summed). The raw number, so «больше 50 списаний» needs no release.
 *  - `isWhale` — the same count compared against the configurable
 *    `billing.whale_min_charges` threshold, resolved once per run by the worker.
 *    Survives a threshold change without editing any pipeline.
 *
 * Both are declared here without operators of their own — the type-derived sets
 * apply, exactly as for every other field.
 */
export const BILLING_FIELDS = withOps([
    { path: 'succeededChargesCount', label: 'Число успешных списаний', type: 'number' },
    { path: 'isWhale', label: 'Клиент — кит', type: 'boolean' },
]);
/** The full catalog, keyed by source. The schema + evaluator both index this. */
export const FIELD_CATALOG = {
    identity: IDENTITY_FIELDS,
    payment: PAYMENT_FIELDS,
    billing: BILLING_FIELDS,
};
/** Look up the `FieldDef` for a `(source, path)` pair, or `undefined` if unknown. */
export const findFieldDef = (source, path) => FIELD_CATALOG[source].find((f) => f.path === path);
/**
 * Read a (flat) path off an object. The catalog uses flat keys, but this also
 * tolerates dotted paths for forward-compatibility. Returns `undefined` for any
 * missing segment or a non-object intermediate.
 */
export const readPath = (obj, path) => {
    if (obj === null || typeof obj !== 'object')
        return undefined;
    let cur = obj;
    for (const seg of path.split('.')) {
        if (cur === null || typeof cur !== 'object')
            return undefined;
        cur = cur[seg];
    }
    return cur;
};
/** Coerce a value to a comparable epoch (ms) for date comparisons, or NaN. */
const toEpoch = (v) => {
    if (v instanceof Date)
        return v.getTime();
    if (typeof v === 'number')
        return v;
    if (typeof v === 'string') {
        const t = Date.parse(v);
        return Number.isNaN(t) ? NaN : t;
    }
    return NaN;
};
/** Coerce a value to a number for numeric comparisons, or NaN. */
const toNumber = (v) => {
    if (typeof v === 'number')
        return v;
    if (typeof v === 'boolean')
        return v ? 1 : 0;
    if (typeof v === 'string' && v.trim() !== '') {
        const n = Number(v);
        return Number.isNaN(n) ? NaN : n;
    }
    return NaN;
};
const isMissing = (v) => v === null || v === undefined;
/**
 * Apply a `field_match` operator. Pure, total (never throws). Semantics:
 *  - `exists` / `not_exists`   — null/undefined aware presence (ignore `value`).
 *  - `is_true` / `is_false`    — strict boolean match (ignore `value`).
 *  - `equals` / `not_equals`   — date→epoch, number→numeric, else `String===`.
 *  - `gt`/`gte`/`lt`/`lte`     — numeric for number, epoch for date, `false` for string.
 *  - `contains`                — `String(actual).includes(String(value))`.
 *  - `in`                      — array membership of `actual` by `String` equality.
 *  - any comparison op with a MISSING actual → `false`.
 */
export const applyOperator = (operator, actual, value, fieldType) => {
    if (operator === 'exists')
        return !isMissing(actual);
    if (operator === 'not_exists')
        return isMissing(actual);
    if (operator === 'is_true')
        return actual === true;
    if (operator === 'is_false')
        return actual === false;
    // All remaining operators compare against `actual` — a missing actual is false.
    if (isMissing(actual))
        return false;
    switch (operator) {
        case 'equals':
        case 'not_equals': {
            let eq;
            if (fieldType === 'date') {
                const a = toEpoch(actual);
                const b = toEpoch(value);
                eq = !Number.isNaN(a) && !Number.isNaN(b) && a === b;
            }
            else if (fieldType === 'number') {
                const a = toNumber(actual);
                const b = toNumber(value);
                eq = !Number.isNaN(a) && !Number.isNaN(b) && a === b;
            }
            else {
                eq = String(actual) === String(value);
            }
            return operator === 'equals' ? eq : !eq;
        }
        case 'gt':
        case 'gte':
        case 'lt':
        case 'lte': {
            // Ordered comparison only for number/date; strings have no order here.
            let a;
            let b;
            if (fieldType === 'date') {
                a = toEpoch(actual);
                b = toEpoch(value);
            }
            else if (fieldType === 'number') {
                a = toNumber(actual);
                b = toNumber(value);
            }
            else {
                return false;
            }
            if (Number.isNaN(a) || Number.isNaN(b))
                return false;
            if (operator === 'gt')
                return a > b;
            if (operator === 'gte')
                return a >= b;
            if (operator === 'lt')
                return a < b;
            return a <= b;
        }
        case 'contains':
            return String(actual).includes(String(value));
        case 'in':
            return Array.isArray(value) && value.some((v) => String(v) === String(actual));
        default:
            return false;
    }
};
//# sourceMappingURL=field-catalog.js.map